import {
  Box,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Button,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useEffect } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { recurringDate } from '@finley-technologies/finley-date';
import { Util, Date as dateType } from '@finley-technologies/finley-types';

import { ConfigFormState } from '../types';
import {
  convertConfigFormStatesToRecurringDateConfigs,
  downloadCSVWithoutHeader,
} from '../utils';
import { Icon } from 'packages/common-frontend/components/icons';

type ReviewDatesComponentProps = {
  config: dateType.RecurringDate.Config;
  setReviewDatesCount: (num: number) => void;
};
export const ReviewDatesComponent = ({
  config,
  setReviewDatesCount,
}: ReviewDatesComponentProps) => {
  const possibleDates = recurringDate.generateDateList(
    config.recurringDateConfigs,
  );
  const possibleExclusions = recurringDate.generateDateList(config.exclusions);

  const validDates = _.difference(possibleDates, possibleExclusions);
  const excludedDates = _.intersection(possibleDates, possibleExclusions);

  useEffect(() => setReviewDatesCount(validDates.length), [
    setReviewDatesCount,
    validDates.length,
  ]);

  return (
    <Stack spacing="20px">
      {validDates && validDates.length > 0 ? (
        <TableContainer>
          <Box overflowY="auto" maxHeight="300px">
            <Table>
              <Thead bg="gray.500">
                <Tr>
                  <Th>Dates</Th>
                  <Th>Days</Th>
                </Tr>
              </Thead>
              <Tbody>
                {_.map(validDates, (date) => {
                  return (
                    <Tr key={`review-dates-key-${date}`}>
                      <Td>{commonDate.fullDateStrToHumanDateString(date)}</Td>
                      <Td>{commonDate.fullDateStrToStr(date, 'ccc')}</Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        </TableContainer>
      ) : (
        <Text fontWeight="light">No valid dates to review.</Text>
      )}

      {excludedDates && excludedDates.length > 0 && (
        <>
          <Text fontSize="md" fontWeight="bold" mt={2}>
            Excluded Dates
          </Text>
          <TableContainer>
            <Box overflowY="auto" maxHeight="300px">
              <Table>
                <Thead bg="gray.500">
                  <Tr>
                    <Th>Dates</Th>
                    <Th>Days</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {_.map(excludedDates, (date) => {
                    return (
                      <Tr key={`review-dates-key-${date}`}>
                        <Td>{commonDate.fullDateStrToHumanDateString(date)}</Td>
                        <Td>{commonDate.fullDateStrToStr(date, 'ccc')}</Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          </TableContainer>
        </>
      )}

      {validDates && validDates.length > 0 && (
        <Button
          size="sm"
          aria-label="download datelist"
          leftIcon={<Icon icon="arrow-down-to-bracket" />}
          width="fit-content"
          variant="ghost"
          onClick={() => {
            downloadCSVWithoutHeader('recurring-ui-dates', validDates);
          }}
        >
          Download Dates
        </Button>
      )}
    </Stack>
  );
};

type ReviewDatesComponentFromConfigFormStateProps = {
  recurringConfigs: ConfigFormState[];
  excludedConfigs: ConfigFormState[];
  manualAddDates: Util.FullDateStr[];
  manualExcludeDates: Util.FullDateStr[];
  setReviewDatesCount: (num: number) => void;
};
export const ReviewDatesComponentFromConfigFormState = ({
  recurringConfigs,
  excludedConfigs,
  manualAddDates,
  manualExcludeDates,
  setReviewDatesCount,
}: ReviewDatesComponentFromConfigFormStateProps) => {
  const allRecurringDates = convertConfigFormStatesToRecurringDateConfigs(
    recurringConfigs.filter((c) => c.isValid && !c.isRelative),
  ).concat(manualAddDates.length > 0 ? [manualAddDates] : []);
  const allExclusionDates = convertConfigFormStatesToRecurringDateConfigs(
    excludedConfigs.filter((c) => c.isValid && !c.isRelative),
  ).concat(manualExcludeDates.length > 0 ? [manualExcludeDates] : []);

  return (
    <ReviewDatesComponent
      config={{
        recurringDateConfigs: allRecurringDates,
        exclusions: allExclusionDates,
      }}
      setReviewDatesCount={setReviewDatesCount}
    />
  );
};
